import React, { useMemo } from 'react';
import { LayersControl, GeoJSON } from 'react-leaflet';
import { getAliasFromNomeTema } from './LayerAliasUtils';
import { isLayerVisibleByDefault } from './VisibleLayersConfig';

export default function LayerGroupManager({ themeGeometries, getStyleForTheme, activeLayers }) {
  // Gera um identificador único baseado no conteúdo das geometrias para forçar rerenderização
  const geometriesHash = useMemo(() => {
    return Object.entries(themeGeometries)
      .map(([tema, geoms]) => `${tema}:${geoms.length}`)
      .join('|');
  }, [themeGeometries]);

  //console.log('[LayerGroupManager] Renderizando com hash:', geometriesHash);
  //console.log('[LayerGroupManager] Temas disponíveis:', Object.keys(themeGeometries));

  // Agrupa geometrias por alias amigável
  const layersByAlias = {};
  Object.entries(themeGeometries).forEach(([tema, geoms]) => {
    geoms.forEach((g) => {
      const alias = getAliasFromNomeTema(tema);
      if (!layersByAlias[alias]) layersByAlias[alias] = [];
      layersByAlias[alias].push(g);
    });
  });

  //console.log('[LayerGroupManager] Camadas por alias:', Object.keys(layersByAlias));

  // Camadas que devem sempre estar presentes no controle de layers
  const standardLayers = [
    'ÁREA DO IMÓVEL',
    'SIGEF',
    'ÁREA CONSOLIDADA',
    'VEGETAÇÃO NATIVA',
    'RESERVA LEGAL',
    'APP',
    'HIDROGRAFIA',
    'ÁREA DE SERVIDÃO',
    'ÁREA DE POUSIO',
    'USO RESTRITO',
  ];

  // Todas as camadas que devem aparecer no controle
  // 1. Primeiro incluímos as camadas padrão que sempre devem aparecer
  // 2. Depois adicionamos camadas personalizadas que foram encontradas nos dados
  const allAliases = [...new Set([...standardLayers, ...Object.keys(layersByAlias)])];

  //console.log('[LayerGroupManager] Todas as camadas a serem renderizadas:', allAliases);
  //console.log('[LayerGroupManager] Estado ativação das camadas:', activeLayers || 'Todas ativas por padrão');

  // Usando a configuração centralizada para camadas que devem estar visíveis por padrão
  // Importado do arquivo de configuração centralizada VisibleLayersConfig.js

  return (
    <>
      {allAliases.map((alias) => {
        const geoms = layersByAlias[alias] || [];
        const hasGeometries = geoms.length > 0;

        // Determina se a camada está ativa
        // 1. Se activeLayers estiver vazio, todas as camadas são consideradas ativas (comportamento padrão)
        // 2. Se for uma camada que deve estar visível por padrão, conforme configuração centralizada, sempre ativa
        // 3. Caso contrário, verifica o estado específico da camada no objeto activeLayers
        const isDefaultVisible = isLayerVisibleByDefault(alias);
        const isActive =
          // Se activeLayers não existir ou estiver vazio, use a configuração padrão
          !activeLayers || Object.keys(activeLayers).length === 0
            ? isDefaultVisible // Apenas as camadas configuradas como visíveis por padrão ficam ativas
            : activeLayers[alias] === true; // Caso contrário, verifica o estado específico da camada

        // Se a camada tem geometrias e está ativa, deve renderizar as geometrias
        const shouldRenderGeometries = isActive && hasGeometries;

        //console.log(`[LayerGroupManager] Camada ${alias}:
        //  - Tem geometrias: ${hasGeometries ? 'Sim' : 'Não'} (${geoms.length})
        //  - Está ativa: ${isActive ? 'Sim' : 'Não'}
        //  - Deve renderizar: ${shouldRenderGeometries ? 'Sim' : 'Não'}`);

        // Processa geometrias (apenas se tiver geometrias)
        let features = [];

        if (hasGeometries) {
          // Processa as geometrias mesmo que não vá renderizar agora
          // (para que estejam prontas quando o usuário ativar a camada)
          //console.log(`[LayerGroupManager] Processando ${geoms.length} geometrias para camada ${alias}`);

          const parsedGeoms = geoms.map((g) => {
            return typeof g === 'string' ? JSON.parse(g) : g;
          });

          features = parsedGeoms.map((geometry) => {
            // Verifica se já não é um Feature
            if (geometry.type === 'Feature') {
              return geometry;
            }

            // Se não for Feature, mas tiver type e coordinates, é uma geometria GeoJSON
            if (geometry.type && geometry.coordinates) {
              return {
                type: 'Feature',
                geometry: geometry,
                properties: {},
              };
            }

            // Se não for uma Feature nem uma geometria GeoJSON válida, tenta corrigir
            if (geometry.geojson) {
              const geojson =
                typeof geometry.geojson === 'string'
                  ? JSON.parse(geometry.geojson)
                  : geometry.geojson;

              return {
                type: 'Feature',
                geometry: geojson,
                properties: {},
              };
            }

            // Caso de falha, retorna uma geometria vazia
            return {
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [0, 0] },
              properties: { error: 'Geometria inválida' },
            };
          });
        }

        // Estilo para a camada
        const style = getStyleForTheme
          ? getStyleForTheme(alias)
          : { color: '#ff0000', weight: 3, fillOpacity: 0.3 };

        // Cria um identificador único para forçar rerenderização
        const key = `${alias}-${geometriesHash}`;

        // Determina se a camada deve ser marcada como checked no controle
        // Camadas definidas em VisibleLayersConfig sempre iniciam ativas, as demais seguem activeLayers
        const isChecked = isDefaultVisible || (activeLayers && activeLayers[alias] === true);

        // Sempre renderiza a LayersControl.Overlay para todas as camadas
        return (
          <LayersControl.Overlay
            key={key}
            name={`${alias} ${hasGeometries ? `(${geoms.length})` : ''}`}
            checked={isChecked}
          >
            {/* Só renderiza o GeoJSON se a camada estiver ativa e tiver geometrias */}
            {shouldRenderGeometries && features.length > 0 && (
              <GeoJSON key={key} data={{ type: 'FeatureCollection', features }} style={style} />
            )}
          </LayersControl.Overlay>
        );
      })}
    </>
  );
}
